import { useCallback } from 'react';
import { AccessConditions, ManagerAccessType, WorkerAccessType } from 'routes';
import { useAccessRegistry, AccessFlagKey } from './useAccessRegistry';

export const useRouteAuthorization = () => {
  const { flags, isMyWLCLoading } = useAccessRegistry();

  const checkAccess = useCallback(
    (access: ManagerAccessType | WorkerAccessType): boolean => {
      if (access === null) return false;
      return flags[access as AccessFlagKey] ?? false;
    },
    [flags],
  );

  const isAllowed = useCallback(
    (onlyShownTo: AccessConditions | readonly (ManagerAccessType | WorkerAccessType)[]) => {
      if (!onlyShownTo) return true;

      // Handle legacy array format
      if (Array.isArray(onlyShownTo)) {
        return onlyShownTo.every((access) => checkAccess(access));
      }

      // Handle new format with anyOf/allOf
      const { anyOf, allOf } = onlyShownTo as AccessConditions;

      // If neither specified, allow access
      if (!anyOf && !allOf) return true;

      // Check anyOf condition (OR)
      const anyOfResult = !anyOf || anyOf.some((access) => checkAccess(access));

      // Check allOf condition (AND)
      const allOfResult = !allOf || allOf.every((access) => checkAccess(access));

      // Both conditions must be satisfied
      return anyOfResult && allOfResult;
    },
    [checkAccess],
  );

  return { isAllowed, checkAccess, isMyWLCLoading };
};
